"use client";

import Link from "next/link";
import {
  Container,
  Title,
  Text,
  Button,
  Stack,
  Group,
  Paper,
  Box,
  Divider,
  Accordion,
  Anchor,
  List,
  ThemeIcon,
  SimpleGrid,
  Badge,
} from "@mantine/core";
import { IconX, IconArrowRight, IconSparkles } from "@tabler/icons-react";
import { PRICING } from "@/constants/pricing";
import { kibbeTypes, kibbeTypeFamilies } from "../kibbe/kibbeData";

type Faq = { question: string; answer: string };

const FAMILY_LABELS: Record<string, string> = {
  dramatic: "Dramatic family",
  natural: "Natural family",
  classic: "Classic family",
  gamine: "Gamine family",
  romantic: "Romantic family",
};

const FAMILY_BLURBS: Record<string, string> = {
  dramatic: "Long vertical line, sharp bones — suits elongated, structured pieces",
  natural: "Broad, blunt bone structure — suits relaxed, unconstructed lines",
  classic: "Balanced, symmetrical — suits clean tailoring and moderate everything",
  gamine: "Compact mix of sharp and soft — suits crisp, playful, broken-up lines",
  romantic: "Soft, rounded, delicate — suits draping, waist emphasis, luxe fabrics",
};

const PILLARS = [
  {
    title: "Vertical line",
    body: "How long your body reads — not just height, but the visual length of your limbs and torso. It decides whether unbroken, elongated lines flatter you or overwhelm you.",
  },
  {
    title: "Bone structure",
    body: "Sharp, blunt, or delicate: the width of your shoulders, the edges of your jaw and hands. Clothes echo bones — sharp shoulders want sharp tailoring, blunt frames want relaxed shapes.",
  },
  {
    title: "Flesh",
    body: "How your body carries softness over the bones. Soft flesh asks for draping and give in the fabric; taut flesh handles structure and crispness.",
  },
];

export default function HowToDressForYourBodyTypeContent({ faqs }: { faqs: Faq[] }) {
  return (
    <Container size="xl" py={48}>
      <Stack gap={48}>
        {/* Masthead */}
        <Box>
          <Divider color="dark.9" size={2} mb={20} />
          <Text fz={11} tt="uppercase" c="dimmed" style={{ letterSpacing: 3 }} mb={{ base: 20, md: 28 }}>
            Body Type Styling Guide &nbsp;&middot;&nbsp; 2026
          </Text>
          <Title order={1} fz={{ base: 36, md: 56 }} fw={300} lh={1.05} style={{ letterSpacing: -2 }} maw={780}>
            How to dress for your body type:{" "}
            <Text span inherit fs="italic" fw={300} c="dark.4">
              lines, not fruit shapes
            </Text>
          </Title>
          <Text fz={{ base: "md", md: "lg" }} c="dark.5" lh={1.65} maw={680} mt={16}>
            Most body type advice sorts you into apple, pear, or hourglass and tells you to hide something. Real
            body type dressing works the other way: it reads your <strong>bone structure and natural lines</strong>{" "}
            and picks clothes that repeat them. That&apos;s the Kibbe system — and it explains why an outfit that
            looks stunning on one &quot;pear&quot; looks wrong on another.
          </Text>
          <Divider color="gray.3" mt={28} />
        </Box>

        {/* Why fruit shapes fail */}
        <Box maw={720}>
          <Title order={2} fz={28} fw={400} mb={12} style={{ letterSpacing: -0.5 }}>
            Why apple/pear advice keeps failing you
          </Title>
          <Text fz="sm" c="dark.5" lh={1.7} mb={12}>
            Fruit-shape systems measure one thing — your waist-to-hip ratio — and give everyone the same goal:
            fake an hourglass. That approach has three built-in problems:
          </Text>
          <List
            spacing={8}
            fz="sm"
            icon={
              <ThemeIcon color="red" size={20} radius="xl" variant="light">
                <IconX size={12} />
              </ThemeIcon>
            }
          >
            <List.Item>
              It ignores height, limb length, shoulder width, and bone sharpness — the things that actually decide
              how a silhouette hangs on you
            </List.Item>
            <List.Item>
              It treats your body as a problem to correct instead of a shape to dress in harmony with
            </List.Item>
            <List.Item>
              Two people with identical ratios can suit opposite clothes — so the advice works for some readers and
              quietly fails the rest
            </List.Item>
          </List>
        </Box>

        {/* The Kibbe approach */}
        <Box>
          <Title order={2} fz={28} fw={400} mb={12} style={{ letterSpacing: -0.5 }}>
            The Kibbe approach: three things to read
          </Title>
          <Text fz="sm" c="dark.5" lh={1.7} maw={720} mb="lg">
            Kibbe&apos;s system scores your body on a yin/yang scale — yang meaning long, sharp, and angular; yin
            meaning soft, rounded, and delicate. It reads three layers, and the balance across them places you in
            one of 13 types, each with its own line logic:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
            {PILLARS.map((pillar, i) => (
              <Paper key={pillar.title} withBorder radius="lg" p="xl">
                <Text fz={11} tt="uppercase" c="dimmed" style={{ letterSpacing: 2 }} mb={6}>
                  {i + 1}
                </Text>
                <Title order={3} fz={20} fw={500} mb={8}>
                  {pillar.title}
                </Title>
                <Text fz="sm" c="dark.5" lh={1.6}>
                  {pillar.body}
                </Text>
              </Paper>
            ))}
          </SimpleGrid>
        </Box>

        {/* All 13 types grid */}
        <Box>
          <Title order={2} fz={28} fw={400} mb={12} style={{ letterSpacing: -0.5 }}>
            The 13 body types — and how each one dresses
          </Title>
          <Text fz="sm" c="dark.5" lh={1.7} maw={720} mb="lg">
            Each type below links to a full outfit guide: what to wear, what to avoid, fabrics, necklines, and
            complete looks built on that type&apos;s lines.
          </Text>
          <Stack gap="lg">
            {Object.entries(kibbeTypeFamilies).map(([family, typeIds]) => (
              <Box key={family}>
                <Group gap={8} mb={8}>
                  <Text fw={600} fz="sm" tt="uppercase" style={{ letterSpacing: 1 }}>
                    {FAMILY_LABELS[family]}
                  </Text>
                  <Text fz="xs" c="dimmed">
                    {FAMILY_BLURBS[family]}
                  </Text>
                </Group>
                <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
                  {typeIds.map((typeId) => {
                    const type = kibbeTypes[typeId];
                    if (!type) return null;
                    return (
                      <Paper
                        key={typeId}
                        component={Link}
                        href={`/kibbe-outfits/${typeId}`}
                        withBorder
                        radius="md"
                        p="md"
                        style={{ textDecoration: "none" }}
                      >
                        <Group justify="space-between" wrap="nowrap" mb={4}>
                          <Text fw={600} fz="sm" c="dark.9">
                            {type.name}
                          </Text>
                          <IconArrowRight size={14} color="var(--mantine-color-dimmed)" />
                        </Group>
                        <Badge size="xs" variant="light" color="gray" mb={6}>
                          {type.yinYang}
                        </Badge>
                        <Text fz="xs" c="dimmed" lh={1.5}>
                          {type.tagline}
                        </Text>
                      </Paper>
                    );
                  })}
                </SimpleGrid>
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Find your type CTA */}
        <Paper radius="lg" p={{ base: 24, md: 36 }} style={{ background: "#f5f3ff", borderTop: "3px solid #141414" }}>
          <Group justify="space-between" align="center" gap="lg" wrap="wrap">
            <Box maw={560}>
              <Text fz={11} tt="uppercase" c="dimmed" style={{ letterSpacing: 2 }} mb={6}>
                Step one
              </Text>
              <Text fz="lg" fw={500} lh={1.4}>
                Don&apos;t know your type yet? The free Kibbe body type test reads your vertical line, bone
                structure, and flesh in about 2 minutes.
              </Text>
            </Box>
            <Button
              component={Link}
              href="/kibbe-body-type-test"
              color="violet"
              size="md"
              rightSection={<IconArrowRight size={16} />}
            >
              Take the free Kibbe test
            </Button>
          </Group>
        </Paper>

        {/* How to apply it */}
        <Box maw={720}>
          <Title order={2} fz={28} fw={400} mb={12} style={{ letterSpacing: -0.5 }}>
            From type to actual outfits
          </Title>
          <Text fz="sm" c="dark.5" lh={1.7} mb={12}>
            Knowing you&apos;re a Soft Natural is step one; dressing like one every morning is the real work. The
            practical path: take the test, read your type&apos;s outfit guide above, then audit your closet against
            the do/don&apos;t lists — most people find a third of their wardrobe was fighting their lines all
            along. Pair the line logic with your{" "}
            <Anchor component={Link} href="/color-analysis-quiz" fz="sm">
              color season
            </Anchor>{" "}
            and you&apos;ve covered the two variables that decide whether an outfit looks like you or looks
            borrowed.
          </Text>
          <Text fz="sm" c="dark.5" lh={1.7}>
            For a deeper primer on the system itself, see the{" "}
            <Anchor component={Link} href="/kibbe-body-types-guide" fz="sm">
              complete Kibbe body types guide
            </Anchor>
            .
          </Text>
        </Box>

        {/* Style Club callout */}
        <Paper radius="lg" p={{ base: 24, md: 36 }} style={{ background: "#f5f3ff", borderTop: "3px solid #141414" }}>
          <Group justify="space-between" align="center" gap="lg" wrap="wrap">
            <Box maw={560}>
              <Text fz={11} tt="uppercase" c="dimmed" style={{ letterSpacing: 2 }} mb={6}>
                Style Club
              </Text>
              <Text fz="lg" fw={500} lh={1.4}>
                Style Club can help you apply these ideas — 3 outfit ideas each week built on your body type and color season, with AI illustrations using your optional photo, matched to your
                city&apos;s weather. ${PRICING.STYLE_CLUB.MONTHLY_PRICE}/month, {PRICING.STYLE_CLUB.TRIAL_DAYS}-day
                trial (card required).
              </Text>
            </Box>
            <Button component={Link} href="/style-club" color="violet" size="md" rightSection={<IconSparkles size={16} />}>
              See how Style Club works
            </Button>
          </Group>
        </Paper>

        <Text fz="sm" c="dimmed" lh={1.6}>
          Style Club: card required for the {PRICING.STYLE_CLUB.TRIAL_DAYS}-day trial. It automatically renews
          at ${PRICING.STYLE_CLUB.MONTHLY_PRICE}/month or ${PRICING.STYLE_CLUB.ANNUAL_PRICE}/year for your
          selected plan. Cancel through the billing link emailed from your member page. Outfit previews are
          AI illustrations, not a fit guarantee; shopping links open searches, not exact product matches.
        </Text>

        {/* FAQs */}
        <Box>
          <Title order={2} fz={28} fw={400} mb="lg" style={{ letterSpacing: -0.5 }}>
            Common questions
          </Title>
          <Accordion variant="separated" radius="md">
            {faqs.map((faq) => (
              <Accordion.Item key={faq.question} value={faq.question}>
                <Accordion.Control fz="sm" fw={500}>
                  {faq.question}
                </Accordion.Control>
                <Accordion.Panel>
                  <Text fz="sm" c="dimmed">
                    {faq.answer}
                  </Text>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Box>

        {/* Related */}
        <Box>
          <Divider mb="lg" />
          <Group gap="sm">
            <Button
              component={Link}
              href="/kibbe-body-type-test"
              variant="default"
              size="xs"
              rightSection={<IconArrowRight size={12} />}
            >
              Free Kibbe body type test
            </Button>
            <Button component={Link} href="/kibbe-outfits" variant="default" size="xs">
              Outfits by body type
            </Button>
            <Button component={Link} href="/kibbe-body-types-guide" variant="default" size="xs">
              Kibbe body types guide
            </Button>
            <Button component={Link} href="/style-club" variant="default" size="xs">
              Style Club
            </Button>
          </Group>
        </Box>
      </Stack>
    </Container>
  );
}
